import { FC, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { DatePickerComponent } from 'ui/DatePickerComponent';
import { getEudrPlans, getEudrRegParser } from 'redux/reducers/eudr/selectors';
import {
	getEudrPlansRequest,
	registerEudrRequest,
	regParseEudrRequest,
} from 'redux/reducers/eudr/reducer';
import { TEudrRegisterDialogProps } from './types';
import { validationSchema } from './validationSchema';

export const EudrRegisterDialog: FC<TEudrRegisterDialogProps> = ({
	open,
	setClose,
	title = 'EUDR Register',
}) => {
	const dispatch = useDispatch();

	const [rawText, setRawText] = useState<string>('');

	const eudrRegParser = useSelector(getEudrRegParser);
	const eudrPlans = useSelector(getEudrPlans);

	const planNameToId = useMemo(
		() => new Map((eudrPlans ?? []).map(plan => [plan.name, plan.id] as const)),
		[eudrPlans],
	);
	const planNameToAmount = useMemo(
		() => new Map((eudrPlans ?? []).map(plan => [plan.name, plan.amount] as const)),
		[eudrPlans],
	);

	const formik = useFormik({
		initialValues: {
			sellingTypeOfProducts: '',
			isSellingToEU: false,
			sellingPlace: '',
			firstName: '',
			lastName: '',
			phoneNumber: '',
			email: '',
			company: '',
			address1: '',
			address2: '',
			cityTown: '',
			stateRegionProvince: '',
			zipCode: '',
			country: '',
			businessRegistrationNumber: '',
			storeAddress: '',
			subscriptionPlan: '',
			periodStart: '',
			periodEnd: '',
			eori: '',
			actualAmount: '' as unknown as number | null,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: values => {
			const subscriptionPlanId = planNameToId.get(values.subscriptionPlan) ?? 0;
			dispatch(
				registerEudrRequest({
					sellingTypeOfProducts: values.sellingTypeOfProducts,
					isSellingToEU: values.isSellingToEU,
					sellingPlace: values.sellingPlace,
					firstName: values.firstName,
					lastName: values.lastName,
					phoneNumber: values.phoneNumber,
					email: values.email,
					company: values.company,
					address1: values.address1,
					address2: values.address2,
					cityTown: values.cityTown,
					stateRegionProvince: values.stateRegionProvince,
					zipCode: values.zipCode,
					country: values.country,
					businessRegistrationNumber: values.businessRegistrationNumber,
					storeAddress: values.storeAddress,
					subscriptionPlanId,
					periodStart: values.periodStart,
					periodEnd: values.periodEnd,
					eori: values.eori,
					actualAmount: Number(values.actualAmount ?? 0),
				}),
			);
			setClose();
		},
	});

	useEffect(() => {
		dispatch(getEudrPlansRequest());
	}, [dispatch]);

	useEffect(() => {
		if (!open) return;
		formik
			.setValues({
				sellingTypeOfProducts: eudrRegParser?.sellingTypeOfProducts ?? '',
				isSellingToEU: eudrRegParser?.isSellingToEU ?? false,
				sellingPlace: eudrRegParser?.sellingPlace ?? '',
				firstName: eudrRegParser?.firstName ?? '',
				lastName: eudrRegParser?.lastName ?? '',
				phoneNumber: eudrRegParser?.phoneNumber ?? '',
				email: eudrRegParser?.email ?? '',
				company: eudrRegParser?.company ?? '',
				address1: eudrRegParser?.address1 ?? '',
				address2: eudrRegParser?.address2 ?? '',
				cityTown: eudrRegParser?.cityTown ?? '',
				stateRegionProvince: eudrRegParser?.stateRegionProvince ?? '',
				zipCode: eudrRegParser?.zipCode ?? '',
				country: eudrRegParser?.country ?? '',
				businessRegistrationNumber: eudrRegParser?.businessRegistrationNumber ?? '',
				storeAddress: eudrRegParser?.storeAddress ?? '',
				subscriptionPlan:
					(eudrPlans ?? []).find(p => p.id === (eudrRegParser?.subscriptionPlanId ?? 0))?.name ??
					'',
				periodStart: eudrRegParser?.periodStart ?? '',
				periodEnd: eudrRegParser?.periodEnd ?? '',
				eori: eudrRegParser?.eori ?? '',
				actualAmount: eudrRegParser?.actualAmount ?? null,
			})
			.catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eudrRegParser]);

	const handleParseText = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setRawText(value);
		dispatch(
			regParseEudrRequest({
				urlEncodedRawRegistrationData: value,
			}),
		);
	};

	const handleDateStart = (date: DateTime | null) => {
		formik.setFieldValue('periodStart', date?.toISODate() ?? '').catch(() => {});
	};

	const handleDateEnd = (date: DateTime | null) => {
		formik.setFieldValue('periodEnd', date?.toISODate() ?? '').catch(() => {});
	};

	const handlePlanChange = (planName: string) => {
		formik.setFieldValue('subscriptionPlan', planName).catch(() => {});
		const amount = planNameToAmount.get(planName);
		if (typeof amount === 'number') {
			formik.setFieldValue('actualAmount', amount).catch(() => {});
		}
	};

	const isStateRegionProvinceError =
		formik.touched.stateRegionProvince && Boolean(formik.errors.stateRegionProvince);

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '1400px',
					maxWidth: '1400px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">{title}</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent sx={{ p: '0 12px 0px 12px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
						<Box
							sx={{
								width: '100%',
								border: '1px solid #016193',
								p: '8px',
								borderRadius: '12px',
								display: 'flex',
								flexDirection: 'column',
								gap: 1,
							}}
						>
							<Typography color="primary" sx={{ fontSize: '0.9rem' }}>
								Paste Typeform email notification:
							</Typography>
							<TextField
								sx={{
									flexGrow: 1,
									display: 'flex',
									'& .MuiInputBase-root': {
										padding: '6px 8px',
										lineHeight: '1.4',
										height: '100%',
										alignItems: 'stretch',
									},
									'& .MuiInputBase-inputMultiline': {
										height: '100% !important',
									},
									'& textarea': {
										height: '100% !important',
										overflow: 'auto',
									},
								}}
								value={rawText}
								onChange={handleParseText}
								type="text"
								fullWidth
								multiline
								// rows={26}
							/>
						</Box>

						<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '14px' }}>
							<FormControlLabel
								control={
									<Checkbox
										id="isSellingToEU"
										name="isSellingToEU"
										checked={formik.values.isSellingToEU}
										onChange={formik.handleChange}
									/>
								}
								label="Is selling to EU"
							/>

							<TextField
								id="sellingTypeOfProducts"
								name="sellingTypeOfProducts"
								size="small"
								label="Selling type of products"
								value={formik.values.sellingTypeOfProducts}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={
									formik.touched.sellingTypeOfProducts &&
									Boolean(formik.errors.sellingTypeOfProducts)
								}
								helperText={
									formik.touched.sellingTypeOfProducts && formik.errors.sellingTypeOfProducts
								}
							/>

							<TextField
								id="sellingPlace"
								name="sellingPlace"
								size="small"
								label="Selling place"
								value={formik.values.sellingPlace}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.sellingPlace && Boolean(formik.errors.sellingPlace)}
								helperText={formik.touched.sellingPlace && formik.errors.sellingPlace}
							/>

							<TextField
								id="firstName"
								name="firstName"
								size="small"
								label="First name"
								value={formik.values.firstName}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.firstName && Boolean(formik.errors.firstName)}
								helperText={formik.touched.firstName && formik.errors.firstName}
							/>

							<TextField
								id="lastName"
								name="lastName"
								size="small"
								label="Last name"
								value={formik.values.lastName}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.lastName && Boolean(formik.errors.lastName)}
								helperText={formik.touched.lastName && formik.errors.lastName}
							/>

							<TextField
								id="phoneNumber"
								name="phoneNumber"
								size="small"
								label="Phone number"
								value={formik.values.phoneNumber}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
								helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
							/>

							<TextField
								id="email"
								name="email"
								size="small"
								label="Email"
								value={formik.values.email}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.email && Boolean(formik.errors.email)}
								helperText={formik.touched.email && formik.errors.email}
							/>

							<TextField
								id="company"
								name="company"
								size="small"
								label="Company"
								value={formik.values.company}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.company && Boolean(formik.errors.company)}
								helperText={formik.touched.company && formik.errors.company}
							/>

							<TextField
								id="address1"
								name="address1"
								size="small"
								label="Address 1"
								value={formik.values.address1}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.address1 && Boolean(formik.errors.address1)}
								helperText={formik.touched.address1 && formik.errors.address1}
							/>

							<TextField
								id="address2"
								name="address2"
								size="small"
								label="Address 2"
								value={formik.values.address2}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.address2 && Boolean(formik.errors.address2)}
								helperText={formik.touched.address2 && formik.errors.address2}
							/>
							<TextField
								id="cityTown"
								name="cityTown"
								size="small"
								label="City/Town"
								value={formik.values.cityTown}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.cityTown && Boolean(formik.errors.cityTown)}
								helperText={formik.touched.cityTown && formik.errors.cityTown}
							/>
						</Box>

						<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '14px' }}>
							<TextField
								id="stateRegionProvince"
								name="stateRegionProvince"
								size="small"
								label="State/Region/Province"
								value={formik.values.stateRegionProvince}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={isStateRegionProvinceError}
								helperText={formik.touched.stateRegionProvince && formik.errors.stateRegionProvince}
							/>

							<TextField
								id="zipCode"
								name="zipCode"
								size="small"
								label="Zip Code"
								value={formik.values.zipCode}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
								helperText={formik.touched.zipCode && formik.errors.zipCode}
							/>

							<TextField
								id="country"
								name="country"
								size="small"
								label="Country"
								value={formik.values.country}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.country && Boolean(formik.errors.country)}
								helperText={formik.touched.country && formik.errors.country}
							/>

							<TextField
								id="businessRegistrationNumber"
								name="businessRegistrationNumber"
								size="small"
								label="Business Registration Number"
								value={formik.values.businessRegistrationNumber}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={
									formik.touched.businessRegistrationNumber &&
									Boolean(formik.errors.businessRegistrationNumber)
								}
								helperText={
									formik.touched.businessRegistrationNumber &&
									formik.errors.businessRegistrationNumber
								}
							/>

							<TextField
								id="storeAddress"
								name="storeAddress"
								size="small"
								label="Store Address"
								value={formik.values.storeAddress}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.storeAddress && Boolean(formik.errors.storeAddress)}
								helperText={formik.touched.storeAddress && formik.errors.storeAddress}
							/>

							<TextField
								id="eori"
								name="eori"
								size="small"
								label="EORI Number"
								value={formik.values.eori}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="text"
								fullWidth
								error={formik.touched.eori && Boolean(formik.errors.eori)}
								helperText={formik.touched.eori && formik.errors.eori}
							/>

							<FormControl fullWidth>
								<InputLabel id="subscriptionPlanLabel">Subscription Plan</InputLabel>
								<Select
									id="subscriptionPlan"
									labelId="subscriptionPlanLabel"
									label="Subscription Plan"
									name="subscriptionPlan"
									value={formik.values.subscriptionPlan}
									onChange={e => handlePlanChange(String(e.target.value))}
									fullWidth
									size="small"
								>
									{eudrPlans?.map?.(plan => (
										<MenuItem key={plan.id} value={plan.name}>
											{plan.name}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									{formik.touched.subscriptionPlan && formik.errors.subscriptionPlan}
								</FormHelperText>
							</FormControl>

							<TextField
								id="actualAmount"
								name="actualAmount"
								size="small"
								label="Subscription Price"
								value={formik.values.actualAmount ?? ''}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								InputProps={{ inputProps: { min: 0, step: '0.01' } }}
								error={Boolean(formik.errors.actualAmount)}
								helperText={formik.errors.actualAmount as string}
							/>

							<DatePickerComponent
								id="periodStart"
								value={DateTime.fromISO(formik.values.periodStart ?? '')}
								onChange={handleDateStart}
								label="Period Start"
							/>

							<DatePickerComponent
								id="periodEnd"
								value={DateTime.fromISO(formik.values.periodEnd ?? '')}
								onChange={handleDateEnd}
								label="Period End"
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setClose();
						}}
					>
						Cancel
					</Button>
					<Button type="submit" autoFocus>
						Send
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
